import { Box, Divider, Stack, SwipeableDrawer, Typography } from "@mui/material";
import { useMemo } from "react";
import CloseIconButton from "@entrilia/shared/components/CloseIconButton";
import SwipePuller from "@entrilia/shared/components/SwipePuller";
import type { DetailColumn, ReportColumn, ReportRow } from "../../../../api/types/widgetTypes";
import TabularReportCellIcon from "../TabularReportCellIcon";
import { getCellValuesForReportRow } from "./tabularReportCellValuesHelper";
import TabularReportCellValuesList from "./TabularReportCellValuesList";

interface Props {
  open: boolean;
  onClose: () => void;
  row: ReportRow;
  columns: ReportColumn[];
  detailColumns?: DetailColumn[];
}

const TabularReportCellDetailsDrawer = ({ open, onClose, row, columns, detailColumns }: Props) => {
  const cellValues = useMemo(() => getCellValuesForReportRow(row, columns), [row, columns]);

  const detailRows = useMemo(
    () => (row.detailRows ?? []).map((detailRow) => getCellValuesForReportRow(detailRow, detailColumns ?? columns)),
    [row.detailRows, detailColumns, columns]
  );

  const firstCellValue = cellValues[0];

  if (firstCellValue === undefined) {
    return null;
  }

  return (
    <SwipeableDrawer
      open={open}
      anchor="bottom"
      disableSwipeToOpen
      slotProps={{
        backdrop: { onClick: onClose },
      }}
      onClose={onClose}
      onOpen={() => undefined}
      swipeAreaWidth={20}
      sx={{ zIndex: 1300, "& .MuiDrawer-paper": { borderTopLeftRadius: 8, borderTopRightRadius: 8 } }}
    >
      <SwipePuller />
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, pt: 3, pb: 1.5 }}>
        <Stack direction="row" sx={{ alignItems: "center", flex: 1, minWidth: 0 }}>
          {firstCellValue.cell?.startIcon && <TabularReportCellIcon icon={firstCellValue.cell.startIcon} />}
          <Typography variant="subtitle1" sx={{ flex: 1, minWidth: 0, width: 0 }}>
            {firstCellValue.cell?.formattedValue || "-"}
          </Typography>
        </Stack>
        <CloseIconButton onClick={onClose} color="secondary" />
      </Box>
      <Divider />
      <Stack spacing={0.5} sx={{ px: 2, py: 2 }}>
        <TabularReportCellValuesList cellValues={cellValues.slice(1)} />
      </Stack>
      {detailRows.length > 0 && (
        <>
          <Divider />
          <Stack spacing={0.5} sx={{ px: 2, py: 2 }}>
            <Typography variant="subtitle1">Details</Typography>
            {detailRows.map((detailCellValues, index) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <Box key={index}>
                {index > 0 && <Divider sx={{ borderStyle: "dashed", my: 1 }} />}
                <TabularReportCellValuesList cellValues={detailCellValues} />
              </Box>
            ))}
          </Stack>
        </>
      )}
    </SwipeableDrawer>
  );
};

export default TabularReportCellDetailsDrawer;
